<template>
  <v-card class="mb-8">
    <inherit-header
      title="出勤時の申告"
      :grouped="grouped"
      :customized="customized"
      :disabled="saving"
      @customize="$emit('customize')"
      @inherit="$emit('inherit')"
    />

    <v-card-text>
      <p class="text-sm mb-1">
        出勤のときにキャストやスタッフが申告する項目(ヘアメ・同伴・送りなど)です。申告の内容と金額は給与計算(ビジネスルール)から使えます。
      </p>
      <p class="text-xs secondary--text mb-3">
        <template v-if="timeRuleEnabled">
          項目ごとに「この申告があるときの出勤時刻」(遅らせる・専用の計算)を決められます。
        </template>
        <template v-else>
          上の「出勤時刻の自動決定」をオンにすると、項目ごとに出勤時刻への影響も決められます。
        </template>
      </p>

      <v-expansion-panels
        v-model="openIndex"
        accordion
        :readonly="!editable"
      >
        <v-expansion-panel
          v-for="(item, index) in items"
          :key="`check-in-item-${item.id}`"
        >
          <v-expansion-panel-header :hide-actions="!editable">
            <div class="d-flex flex-wrap align-center">
              <span class="font-weight-semibold check-in-item__name">{{ item.name }}</span>

              <span
                v-if="timeRuleEnabled"
                class="check-in-tag"
                :class="`check-in-tag--${item.timeEffect}`"
              >
                {{ effectSummary(item) }}
              </span>

              <span
                class="check-in-tag"
                :class="hasAmount(item) ? 'check-in-tag--amount' : 'check-in-tag--none'"
              >
                {{ hasAmount(item) ? `${Number(item.defaultAmount).toLocaleString()}円` : '金額なし' }}
              </span>

              <span
                v-if="!item.castDeclarable"
                class="check-in-tag check-in-tag--none"
              >
                お店だけが付ける
              </span>

              <span
                v-if="item.oncePerBusinessDay"
                class="check-in-tag check-in-tag--none"
              >
                1日1回まで
              </span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content v-if="editable && drafts[item.id]">
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="drafts[item.id].name"
                  label="名前"
                  hide-details
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="drafts[item.id].defaultAmount"
                  label="金額(空なら金額なし)"
                  type="number"
                  min="0"
                  step="100"
                  suffix="円"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-switch
              v-model="drafts[item.id].castDeclarable"
              label="キャストが自分で申告できる"
              hide-details
              inset
            />

            <v-switch
              v-model="drafts[item.id].oncePerBusinessDay"
              label="1営業日に1回まで(同じ日に2回出勤しても、2回目では申告できない)"
              hide-details
              inset
            />

            <template v-if="timeRuleEnabled">
              <div class="text-sm font-weight-semibold mt-6">
                この申告があるときの出勤時刻
              </div>

              <v-radio-group
                v-model="drafts[item.id].timeEffect"
                hide-details
                class="mt-2"
              >
                <v-radio
                  label="変えない"
                  value="none"
                />

                <v-radio value="add_minutes">
                  <template #label>
                    <div class="d-flex flex-wrap align-center">
                      通常の計算より
                      <v-text-field
                        v-model="drafts[item.id].effectAddMinutes"
                        type="number"
                        min="0"
                        step="5"
                        dense
                        hide-details
                        class="check-in-item__minutes mx-2 mt-0 pt-0"
                        @click.stop
                      />
                      分遅らせる
                    </div>
                  </template>
                </v-radio>

                <v-radio
                  label="通常の計算の代わりに、専用の計算を使う"
                  value="override"
                />
              </v-radio-group>

              <div
                v-if="drafts[item.id].timeEffect === 'override'"
                class="check-in-item__override mt-3"
              >
                <div class="text-sm font-weight-semibold">
                  「{{ drafts[item.id].name }}」のときの出勤時刻の計算
                </div>

                <v-row>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <v-select
                      v-model="drafts[item.id].overrideRoundingMethod"
                      :items="roundingMethods"
                      item-text="text"
                      item-value="value"
                      label="丸め方法"
                      hide-details
                    />
                  </v-col>

                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <v-text-field
                      v-model="drafts[item.id].overrideAddMinutes"
                      label="丸めた後に足す時間"
                      type="number"
                      min="0"
                      step="5"
                      suffix="分"
                      hide-details
                    />
                  </v-col>
                </v-row>

                <v-switch
                  v-model="drafts[item.id].overrideAppliesEarliest"
                  label="「出勤を付け始める時刻」より前にはしない"
                  hide-details
                  inset
                />

                <p class="text-xs mt-3 mb-0">
                  「{{ drafts[item.id].name }}」のときは、通常の計算と、ほかの申告による「遅らせる」は使いません。
                </p>
              </div>
            </template>

            <div class="d-flex flex-wrap align-center mt-6">
              <v-btn
                color="primary"
                :ripple="false"
                :disabled="saving || !drafts[item.id].name"
                @click="save(item)"
              >
                この項目を保存
              </v-btn>

              <v-spacer />

              <v-btn
                icon
                :disabled="saving || index === 0"
                aria-label="上へ"
                @click="move(index, -1)"
              >
                <v-icon>{{ icons.mdiArrowUp }}</v-icon>
              </v-btn>

              <v-btn
                icon
                :disabled="saving || index === items.length - 1"
                aria-label="下へ"
                @click="move(index, 1)"
              >
                <v-icon>{{ icons.mdiArrowDown }}</v-icon>
              </v-btn>

              <v-btn
                icon
                :disabled="saving"
                aria-label="削除"
                @click="deleting = item"
              >
                <v-icon>{{ icons.mdiTrashCanOutline }}</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <p
        v-if="items.length === 0"
        class="text-sm secondary--text mb-0"
      >
        申告項目はまだありません。
      </p>

      <div
        v-if="editable"
        class="d-flex flex-wrap align-center mt-4"
      >
        <span class="text-xs secondary--text mr-3">項目を追加</span>

        <v-btn
          v-for="preset in availablePresets"
          :key="`check-in-preset-${preset.presetKey}`"
          small
          outlined
          color="primary"
          class="mr-2 my-1"
          :ripple="false"
          :disabled="saving"
          @click="$emit('create', preset)"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiPlus }}
          </v-icon>
          {{ preset.name }}
        </v-btn>

        <v-btn
          small
          text
          color="secondary"
          class="my-1"
          :ripple="false"
          :disabled="saving"
          @click="$emit('create', { name: '新しい項目' })"
        >
          自分で作る
        </v-btn>
      </div>
    </v-card-text>

    <v-dialog
      :value="!!deleting"
      width="420"
      @input="deleting = null"
    >
      <v-card v-if="deleting">
        <v-card-title>「{{ deleting.name }}」を削除しますか</v-card-title>

        <v-card-text>
          すでに付いている申告は残ります。これからの出勤では選べなくなります。
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            :ripple="false"
            @click="confirmDelete"
          >
            削除
          </v-btn>

          <v-btn
            text
            color="secondary"
            :ripple="false"
            @click="deleting = null"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  ref,
  computed,
  watch,
} from '@vue/composition-api'
import {
  mdiPlus,
  mdiArrowUp,
  mdiArrowDown,
  mdiTrashCanOutline,
} from '@mdi/js'
import InheritHeader from './InheritHeader.vue'

export const ROUNDING_METHODS = [
  { text: '切り捨て', value: 'floor' },
  { text: '切り上げ', value: 'ceil' },
  { text: '四捨五入', value: 'round' },
]

export default {
  components: {
    InheritHeader,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    presets: {
      type: Array,
      default: () => [],
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    customized: {
      type: Boolean,
      default: false,
    },

    // 出勤時刻の自動決定がオンか。オフなら「出勤時刻」の選択肢を出さない
    timeRuleEnabled: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const openIndex = ref(null)
    const deleting = ref(null)
    const drafts = ref({})

    // グループに入っている店舗は、個別にしてからでないと編集できない
    const editable = computed(() => !props.grouped || props.customized)

    const buildDrafts = () => {
      const next = {}
      props.items.forEach(item => {
        next[item.id] = {
          ...item,
          overrideRoundingMethod: item.overrideRoundingMethod || 'ceil',
        }
      })
      drafts.value = next
    }
    watch(() => props.items, buildDrafts, { immediate: true })

    const availablePresets = computed(() => {
      const used = props.items.map(item => item.presetKey)

      return props.presets.filter(preset => !used.includes(preset.presetKey))
    })

    const hasAmount = item => item.defaultAmount !== null && item.defaultAmount !== undefined

    const methodText = value => (ROUNDING_METHODS.find(method => method.value === value) || {}).text

    const effectSummary = item => {
      if (item.timeEffect === 'add_minutes') return `出勤時刻を${item.effectAddMinutes}分遅らせる`
      if (item.timeEffect === 'override') {
        const add = Number(item.overrideAddMinutes) > 0 ? ` + ${item.overrideAddMinutes}分` : 'のみ'

        return `出勤時刻は専用の計算(${methodText(item.overrideRoundingMethod)}${add})`
      }

      return '出勤時刻は変えない'
    }

    const save = item => {
      const draft = drafts.value[item.id]

      emit('update', {
        id: item.id,
        item: {
          name: draft.name,
          defaultAmount: draft.defaultAmount === '' || draft.defaultAmount === null ? null : Number(draft.defaultAmount),
          castDeclarable: draft.castDeclarable,
          oncePerBusinessDay: draft.oncePerBusinessDay,
          timeEffect: draft.timeEffect,
          effectAddMinutes: Number(draft.effectAddMinutes) || 0,
          overrideRoundingMethod: draft.overrideRoundingMethod,
          overrideAddMinutes: Number(draft.overrideAddMinutes) || 0,
          overrideAppliesEarliest: draft.overrideAppliesEarliest,
        },
      })
    }

    const move = (index, offset) => {
      const ids = props.items.map(item => item.id)
      const target = index + offset
      if (target < 0 || target >= ids.length) return

      const [moved] = ids.splice(index, 1)
      ids.splice(target, 0, moved)
      openIndex.value = null
      emit('reorder', ids)
    }

    const confirmDelete = () => {
      emit('delete', deleting.value.id)
      deleting.value = null
      openIndex.value = null
    }

    return {
      openIndex,
      deleting,
      drafts,
      editable,
      availablePresets,
      roundingMethods: ROUNDING_METHODS,

      hasAmount,
      effectSummary,
      save,
      move,
      confirmDelete,

      icons: {
        mdiPlus,
        mdiArrowUp,
        mdiArrowDown,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>

<style scoped>
.check-in-item__name {
  min-width: 5em;
}
.check-in-item__minutes {
  max-width: 72px;
}
.check-in-item__override {
  border-left: 2px solid #9155fd;
  background: rgba(145, 85, 253, 0.1);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
}

/* Vuetify の color pack は無効のため、色は独自クラスで指定する */
.check-in-tag {
  display: inline-block;
  border-radius: 999px;
  padding: 2px 10px;
  margin: 2px 0 2px 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.check-in-tag--none {
  background: rgba(58, 53, 65, 0.08);
  color: rgba(58, 53, 65, 0.68);
}
.check-in-tag--add_minutes {
  background: rgba(145, 85, 253, 0.1);
  color: #6b35d9;
}
.check-in-tag--override {
  background: rgba(255, 180, 0, 0.14);
  color: #8a6100;
}
.check-in-tag--amount {
  background: rgba(22, 177, 255, 0.12);
  color: #0a6e9f;
}
</style>
